import React, { useEffect, useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import Container from '@/components/ui/container';
import Layout from 'src/layout';
import { useNavigate } from 'react-router-dom';
import InputUi from '@/components/ui/inputui';
import TableSkeleton from '@/components/ui/tableSkeleton';
import { getPaginatedOffers, getOfferCount, deleteOffer } from '../../lib/api/offerApi';
import {
    RiSearchLine,
    RiRefreshLine,
    RiAddLine,
    RiGiftLine,
    RiPercentLine,
    RiCloseLine,
    RiEditLine,
    RiEyeLine,
    RiDeleteBinLine
} from 'react-icons/ri';
import { toast } from 'react-toastify';

const ListOffers = () => {
    const navigate = useNavigate();

    const [offers, setOffers] = useState([]);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [page, setPage] = useState(1);

    const [filters, setFilters] = useState({
        offerID: '',
        offerName: '',
        offerType: ''
    });

    const limit = 10;

    const handleChange = (e, name) => {
        setFilters({ ...filters, [name]: e.target.value });
    };

    const fetchOfferCount = async () => {
        try {
            const { total } = await getOfferCount(filters);
            setTotalPages(Math.ceil((total || 0) / limit) || 1);
        } catch (error) {
            console.error('Error counting offers:', error);
        }
    };

    const fetchOffers = async () => {
        try {
            setLoading(true);
            setRefreshing(true);

            const response = await getPaginatedOffers({
                page,
                limit,
                filters
            });

            const offersArray = Array.isArray(response?.data) ? response.data : [];
            setOffers(offersArray);
        } catch (error) {
            console.error('Error fetching offers:', error);
            setOffers([]);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    const handleSearch = async () => {
        setPage(1);
        await fetchOfferCount();
        await fetchOffers();
    };

    const handleRefresh = async () => {
        await fetchOfferCount();
        await fetchOffers();
    };

    const handleClearFilters = () => {
        setFilters({
            offerID: '',
            offerName: '',
            offerType: ''
        });
        setPage(1);
    };

    const handleDeleteOffer = async (offerID, offerName) => {
        if (!window.confirm(`Are you sure you want to delete the offer "${offerName || offerID}"? This will also remove the offer from all associated products. This action cannot be undone.`)) {
            return;
        }

        try {
            const response = await deleteOffer(offerID);
            if (response.success) {
                toast.success('Offer deleted successfully');
                await fetchOfferCount();
                await fetchOffers();
            } else {
                toast.error(response.message || 'Failed to delete offer');
            }
        } catch (error) {
            console.error('Error deleting offer:', error);
            toast.error(error.response?.data?.message || 'Failed to delete offer');
        }
    };

    useEffect(() => {
        fetchOffers();
        fetchOfferCount();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page]);

    return (
        <Layout title="Offer List" active="admin-offers-list">
            <div className="min-h-screen bg-slate-50/50 p-6 space-y-6">
                <Container>
                    {/* Header Section */}
                    <div className="mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                        <div>
                            <h1 className="text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
                                <RiGiftLine className="text-emerald-600" /> Offer Management
                            </h1>
                            <p className="text-slate-500 text-sm mt-1">
                                Configure promotional deals, Buy-X-Get-Y offers, and product discounts.
                            </p>
                        </div>
                        <div className="flex items-center gap-3">
                            <Button
                                onClick={handleRefresh}
                                disabled={refreshing}
                                variant="outline"
                                className="flex items-center gap-2 bg-white hover:bg-slate-100 border-slate-200 text-slate-700 shadow-sm"
                            >
                                <RiRefreshLine className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                                Refresh
                            </Button>
                            <Button
                                onClick={() => navigate('/offer/add')}
                                className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm font-medium"
                            >
                                <RiAddLine className="w-4 h-4" />
                                Add Offer
                            </Button>
                        </div>
                    </div>

                    {/* Stats Cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                        <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm hover:shadow-md transition-all duration-200">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Total Offers</p>
                                    <p className="text-2xl font-extrabold text-slate-900 mt-1">{offers.length}</p>
                                </div>
                                <div className="p-3 bg-emerald-50 rounded-xl">
                                    <RiGiftLine className="w-6 h-6 text-emerald-600" />
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm hover:shadow-md transition-all duration-200">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Buy X Get Y</p>
                                    <p className="text-2xl font-extrabold text-emerald-600 mt-1">
                                        {offers.filter(o => o.offerType === 'buy_x_get_y').length}
                                    </p>
                                </div>
                                <div className="p-3 bg-green-50 rounded-xl">
                                    <RiGiftLine className="w-6 h-6 text-green-600" />
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm hover:shadow-md transition-all duration-200">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Buy X At ₹X</p>
                                    <p className="text-2xl font-extrabold text-blue-600 mt-1">
                                        {offers.filter(o => o.offerType === 'buy_x_at_x').length}
                                    </p>
                                </div>
                                <div className="p-3 bg-blue-50 rounded-xl">
                                    <RiPercentLine className="w-6 h-6 text-blue-600" />
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm hover:shadow-md transition-all duration-200">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Discount OFF</p>
                                    <p className="text-2xl font-extrabold text-teal-600 mt-1">
                                        {offers.filter(o => o.offerType === 'buy_x_get_off').length}
                                    </p>
                                </div>
                                <div className="p-3 bg-teal-50 rounded-xl">
                                    <RiPercentLine className="w-6 h-6 text-teal-600" />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Search and Filters */}
                    <div className="bg-white rounded-xl p-5 mb-6 border border-slate-200 shadow-sm space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="relative">
                                <RiSearchLine className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                                <InputUi
                                    placeholder="Search by Offer ID..."
                                    value={filters.offerID}
                                    datafunction={(e) => handleChange(e, 'offerID')}
                                    className="pl-9"
                                />
                            </div>
                            <div className="relative">
                                <RiGiftLine className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                                <InputUi
                                    placeholder="Search by Offer Name..."
                                    value={filters.offerName}
                                    datafunction={(e) => handleChange(e, 'offerName')}
                                    className="pl-9"
                                />
                            </div>
                            <div className="relative">
                                <select
                                    value={filters.offerType}
                                    onChange={(e) => handleChange(e, 'offerType')}
                                    className="w-full h-[42px] border border-slate-200 rounded-lg px-3 text-sm bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                                >
                                    <option value="">All Offer Types</option>
                                    <option value="buy_x_get_y">Buy X Get Y</option>
                                    <option value="buy_x_at_x">Buy X At ₹X</option>
                                    <option value="buy_x_get_off">Buy X Get OFF</option>
                                </select>
                            </div>
                        </div>

                        <div className="flex items-center gap-3">
                            <Button
                                onClick={handleSearch}
                                disabled={loading}
                                className="px-5 bg-emerald-600 hover:bg-emerald-700 text-white font-medium shadow-sm"
                            >
                                {loading ? 'Searching...' : 'Search Offers'}
                            </Button>
                            <Button
                                onClick={handleClearFilters}
                                variant="outline"
                                className="px-4 text-slate-600 border-slate-200 hover:bg-slate-50"
                            >
                                <RiCloseLine className="w-4 h-4 mr-1.5" />
                                Clear Filters
                            </Button>
                        </div>
                    </div>

                    {/* Main Table */}
                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                            <h3 className="text-base font-semibold text-slate-800">All Offers</h3>
                            <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2.5 py-1 rounded-full">
                                {offers.length} loaded
                            </span>
                        </div>

                        <div className="overflow-x-auto">
                            <Table className="w-full">
                                <TableHeader>
                                    <TableRow className="bg-slate-50/80 border-b border-slate-200">
                                        <TableHead className="px-6 py-3.5 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">
                                            Offer ID
                                        </TableHead>
                                        <TableHead className="px-6 py-3.5 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">
                                            Offer Name
                                        </TableHead>
                                        <TableHead className="px-6 py-3.5 text-center text-xs font-bold text-slate-600 uppercase tracking-wider">
                                            Type & Rule
                                        </TableHead>
                                        <TableHead className="px-6 py-3.5 text-center text-xs font-bold text-slate-600 uppercase tracking-wider">
                                            Buy Requirement
                                        </TableHead>
                                        <TableHead className="px-6 py-3.5 text-center text-xs font-bold text-slate-600 uppercase tracking-wider">
                                            Reward Details
                                        </TableHead>
                                        <TableHead className="px-6 py-3.5 text-center text-xs font-bold text-slate-600 uppercase tracking-wider">
                                            Actions
                                        </TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody className="divide-y divide-slate-100">
                                    {loading ? (
                                        <TableSkeleton columns={6} rows={5} />
                                    ) : offers.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={6} className="px-6 py-12 text-center">
                                                <div className="flex flex-col items-center justify-center">
                                                    <RiGiftLine className="w-12 h-12 text-slate-300 mb-3" />
                                                    <p className="text-slate-700 font-semibold text-base">No offers found</p>
                                                    <p className="text-slate-400 text-xs mt-1">Try adjusting your search query or clear filters.</p>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        offers.map((offer, idx) => (
                                            <TableRow
                                                key={offer.offerID || idx}
                                                className="hover:bg-slate-50/80 transition-colors"
                                            >
                                                <TableCell className="px-6 py-4 whitespace-nowrap">
                                                    <div className="flex items-center gap-3">
                                                        <div className="h-9 w-9 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold text-xs">
                                                            {String(offer.offerID || '').slice(-3) || 'OFF'}
                                                        </div>
                                                        <span className="text-sm font-mono font-medium text-slate-900">
                                                            {offer.offerID || 'N/A'}
                                                        </span>
                                                    </div>
                                                </TableCell>
                                                <TableCell className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm font-semibold text-slate-900">
                                                        {offer.offerName || 'Unnamed Offer'}
                                                    </div>
                                                </TableCell>
                                                <TableCell className="px-6 py-4 whitespace-nowrap text-center">
                                                    <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${
                                                        offer.offerType === 'buy_x_get_y'
                                                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                                            : offer.offerType === 'buy_x_get_off'
                                                            ? 'bg-purple-50 text-purple-700 border border-purple-200'
                                                            : 'bg-blue-50 text-blue-700 border border-blue-200'
                                                    }`}>
                                                        {offer.offerType === 'buy_x_get_off'
                                                            ? `BUY ${offer.buyCount} GET ${offer.discountValue}${offer.discountType === 'percentage' ? '%' : '₹'} OFF`
                                                            : (offer.offerType ? offer.offerType.replace(/_/g, ' ').toUpperCase() : 'UNKNOWN')}
                                                    </span>
                                                </TableCell>
                                                <TableCell className="px-6 py-4 whitespace-nowrap text-center">
                                                    <span className="text-sm font-bold text-slate-800">
                                                        Buy {offer.buyCount || 1}
                                                    </span>
                                                </TableCell>
                                                <TableCell className="px-6 py-4 whitespace-nowrap text-center">
                                                    <span className="text-sm font-medium text-slate-700">
                                                        {offer.offerType === 'buy_x_get_off'
                                                            ? (offer.productScope === 'same_product' ? 'Same Item' : 'Mix & Match')
                                                            : offer.offerType === 'buy_x_at_x'
                                                            ? `At ₹${offer.buyAt || 0}`
                                                            : `Get ${offer.getCount || 1} Free`}
                                                    </span>
                                                </TableCell>
                                                <TableCell className="px-6 py-4 whitespace-nowrap text-center">
                                                    <div className="flex items-center justify-center gap-2">
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            onClick={() => navigate(`/offer/details/${offer.offerID}`)}
                                                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-blue-600 border-blue-200 hover:bg-blue-50"
                                                        >
                                                            <RiEditLine className="w-3.5 h-3.5" />
                                                            Edit
                                                        </Button>
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            onClick={() => handleDeleteOffer(offer.offerID, offer.offerName)}
                                                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-rose-600 border-rose-200 hover:bg-rose-50"
                                                        >
                                                            <RiDeleteBinLine className="w-3.5 h-3.5" />
                                                            Delete
                                                        </Button>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between">
                                <span className="text-xs text-slate-500">
                                    Page <strong className="text-slate-800">{page}</strong> of <strong className="text-slate-800">{totalPages}</strong>
                                </span>
                                <div className="flex items-center gap-2">
                                    <Button
                                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                                        disabled={page === 1 || loading}
                                        variant="outline"
                                        size="sm"
                                        className="text-xs h-8 px-3 disabled:opacity-50"
                                    >
                                        Previous
                                    </Button>
                                    <Button
                                        onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
                                        disabled={page === totalPages || loading}
                                        variant="outline"
                                        size="sm"
                                        className="text-xs h-8 px-3 disabled:opacity-50"
                                    >
                                        Next
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                </Container>
            </div>
        </Layout>
    );
};

export default ListOffers;
